/**
 * FdaLabelPdfViewerEmbed — Thin wrapper around `RegulatoryViewerEmbed`
 * bound to the US Drugs@FDA label-PDF family's API client.
 *
 * Mirrors AUViewerEmbed / SBAViewerEmbed, NOT DePdfViewerEmbed. de_pdf is the
 * closest family by wiring (same shared backend builders, same admin-only
 * canary gate), but its viewer is `BilingualPdfViewerEmbed`, whose config
 * REQUIRES a `translate` callback and renders a source block beside an
 * English panel. This family's source text is already English — the backend
 * mounts no `/fda_label_pdf/translate` route at all — so a bilingual viewer
 * here would need a fake translate and would render a panel duplicating the
 * page. Monolingual is the correct shape.
 *
 * Per-page extraction state (`is_ocr` / `extraction_tier` /
 * `location_status`) rides on the compare payload's page objects, not here:
 * the viewer reads pages through `viewerApi`, and surfacing "this page's
 * highlight is approximate" is a per-page caveat the compare view owns.
 */
import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { ChunkMetadata, TOCEntry } from '../../types/viewer';
import { fdaLabelPdfApi } from '../../api/createViewerApi';
import RegulatoryViewerEmbed, {
  type ChunkAnchor,
  type RegulatoryViewerHandle,
} from './RegulatoryViewerEmbed';

export type FdaLabelPdfViewerHandle = RegulatoryViewerHandle;

interface FdaLabelPdfViewerEmbedProps {
  docId: number;
  highlightChunkIds?: number[];
  chunkAnchors?: Record<number, ChunkAnchor[]>;
  /** page_num -> anchor for a chunk-free (progressive-read) citation.
   *  Forwarded verbatim to RegulatoryViewerEmbed via the props spread. */
  pageAnchors?: Record<number, ChunkAnchor[]> | undefined;
  highlightTerm?: string | null;
  highlightTerms?: string[] | null;
  englishExact?: boolean;
  highlightColors?: (string | undefined)[] | null;
  landingPage?: number | undefined;
  onChunkClick?: (chunk: ChunkMetadata) => void;
  visibleSectionNumbers?: string[] | undefined;
  onTocLoaded?: ((entries: TOCEntry[]) => void) | undefined;
}

const FdaLabelPdfViewerEmbed = forwardRef<
  FdaLabelPdfViewerHandle,
  FdaLabelPdfViewerEmbedProps
>(function FdaLabelPdfViewerEmbed(props, ref) {
  const innerRef = useRef<RegulatoryViewerHandle>(null);
  useImperativeHandle(ref, () => ({
    scrollToPage: (n) => innerRef.current?.scrollToPage(n),
    highlightChunks: (ids) => innerRef.current?.highlightChunks(ids),
    scrollToSection: (s, opts) =>
      innerRef.current?.scrollToSection(s, opts) ?? false,
  }));
  return (
    <RegulatoryViewerEmbed ref={innerRef} viewerApi={fdaLabelPdfApi} {...props} />
  );
});

export default FdaLabelPdfViewerEmbed;
